import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DOMPurify from "dompurify";

/**
 * Renders AI-generated or instructor-authored lesson content.
 * Supports three content shapes:
 *   1. HTML (TipTap output) — sanitised and rendered directly.
 *   2. Markdown (legacy AI output) — rendered with GitHub-flavoured markdown.
 *   3. Plain text — whitespace preserved.
 */
const MD_MARKERS = /(^|\n)(#{1,6}\s|[-*]\s|\d+\.\s|>\s|\*\*|__)/;
const HTML_TAG = /<\/?[a-z][\s\S]*?>/i;

const ALLOWED_TAGS = [
    "p", "br", "strong", "b", "em", "i", "u", "s", "strike", "del",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "ul", "ol", "li", "blockquote",
    "a", "img", "iframe", "hr", "code", "pre", "span", "div",
    "table", "thead", "tbody", "tr", "th", "td",
];
const ALLOWED_ATTR = [
    "href", "target", "rel", "src", "alt", "title", "width", "height",
    "allow", "allowfullscreen", "frameborder",
];

export default function LessonContent({ content }) {
    const sanitised = useMemo(() => {
        if (!content || !HTML_TAG.test(content)) return null;
        return DOMPurify.sanitize(content, {
            ALLOWED_TAGS,
            ALLOWED_ATTR,
            ALLOWED_URI_REGEXP: /^(https?:|mailto:|tel:|#|\/)/i,
            ADD_ATTR: ["target"],
        });
    }, [content]);

    if (!content) return null;

    if (sanitised !== null) {
        return (
            <div
                className="lesson-html"
                data-testid="lesson-content-html"
                dangerouslySetInnerHTML={{ __html: sanitised }}
            />
        );
    }

    const hasMarkdown = MD_MARKERS.test(content);

    if (!hasMarkdown) {
        return (
            <div
                className="whitespace-pre-wrap text-foreground/85 leading-relaxed text-base"
                data-testid="lesson-content-plain"
            >
                {content}
            </div>
        );
    }

    return (
        <div className="lesson-prose" data-testid="lesson-content-markdown">
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    h1: ({ node, ...props }) => (
                        <h2
                            className="font-display text-2xl text-primary mt-8 mb-3"
                            {...props}
                        />
                    ),
                    h2: ({ node, ...props }) => (
                        <h3
                            className="font-display text-xl text-primary mt-7 mb-2"
                            {...props}
                        />
                    ),
                    h3: ({ node, ...props }) => (
                        <h4
                            className="font-display text-lg text-primary mt-5 mb-2"
                            {...props}
                        />
                    ),
                    p: ({ node, ...props }) => (
                        <p
                            className="text-foreground/85 leading-relaxed my-3"
                            {...props}
                        />
                    ),
                    ul: ({ node, ...props }) => (
                        <ul
                            className="list-disc pl-6 my-3 space-y-1 text-foreground/85"
                            {...props}
                        />
                    ),
                    ol: ({ node, ...props }) => (
                        <ol
                            className="list-decimal pl-6 my-3 space-y-1 text-foreground/85"
                            {...props}
                        />
                    ),
                    li: ({ node, ...props }) => (
                        <li className="leading-relaxed" {...props} />
                    ),
                    strong: ({ node, ...props }) => (
                        <strong className="text-foreground font-semibold" {...props} />
                    ),
                    em: ({ node, ...props }) => (
                        <em className="italic text-foreground/90" {...props} />
                    ),
                    blockquote: ({ node, ...props }) => (
                        <blockquote
                            className="my-4 pl-4 border-l-4 border-accent bg-accent/5 py-3 pr-3 rounded-sm text-foreground/85 italic"
                            {...props}
                        />
                    ),
                    hr: () => <hr className="my-6 border-border" />,
                    table: ({ node, ...props }) => (
                        <div className="my-4 overflow-x-auto">
                            <table className="w-full text-sm border-collapse" {...props} />
                        </div>
                    ),
                    th: ({ node, ...props }) => (
                        <th
                            className="text-left font-semibold border-b border-border px-2 py-1.5 bg-secondary/40"
                            {...props}
                        />
                    ),
                    td: ({ node, ...props }) => (
                        <td
                            className="border-b border-border px-2 py-1.5 text-foreground/85"
                            {...props}
                        />
                    ),
                    code: ({ inline, ...props }) =>
                        inline ? (
                            <code
                                className="bg-secondary/60 text-primary rounded px-1.5 py-0.5 text-sm"
                                {...props}
                            />
                        ) : (
                            <code
                                className="block bg-secondary/60 text-foreground rounded-sm p-3 my-3 text-sm overflow-x-auto"
                                {...props}
                            />
                        ),
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
}
